import JumboCardQuick from '@jumbo/components/JumboCardQuick';
import { Box, Button, Card, FormControl, Grid, InputLabel, MenuItem, Select, TextField, Switch, FormControlLabel, FormLabel, Typography, OutlinedInput, Autocomplete } from "@mui/material";
import { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import Swal from "sweetalert2";
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import moment from 'moment';
import config from '../../../config';
import { postSaveAnnouncementApi, putUpdateAnnouncementApi } from 'api/announcement';

const EDITOR_EXTENSIONS = [
  StarterKit.configure({
    heading: { levels: [1, 2, 3] },
  }),
  Link.configure({ openOnClick: false, HTMLAttributes: { rel: 'noopener' } }),
  Placeholder.configure({ placeholder: 'Duyuru içeriğini buraya yazın...' }),
];

function AddUpdateAnnouncementCard({ afterUpdateFunc, selectedAnnouncement, onClose, addAfterTable }) {

    const empty_announcementObj = {
        title: '',
        body: '',
        status: 1,
        addedDate: moment().format('YYYY-MM-DD HH:mm:ss'),
        updatedDate: moment().format('YYYY-MM-DD HH:mm:ss'),
        fromAnnouncement: 'Kurum',
        toAnnouncement: config.institutions.map(x => x.inst)
    };
    const [announcementObj, setAnnouncementObj] = useState(empty_announcementObj);
    const { authUser } = useSelector(state => state.auth);

    const editor = useEditor({
        extensions: EDITOR_EXTENSIONS,
        content: announcementObj.body || '',
        editorProps: {
            attributes: {
                class: 'add-update-announcement-editor',
                'data-placeholder': 'Duyuru içeriğini buraya yazın...',
            },
        },
        onUpdate: ({ editor }) => {
            setAnnouncementObj((prev) => ({
                ...prev,
                body: editor.getHTML(),
            }));
        },
    });

    useEffect(() => {
        if (editor) {
            const externalBody = selectedAnnouncement ? (selectedAnnouncement.body || '') : '';
            editor.commands.setContent(externalBody, false);
        }
    }, [selectedAnnouncement, editor]);

    useEffect(() => {
        if (selectedAnnouncement) {
            const updatedAnnouncement = { ...selectedAnnouncement };
            if (typeof selectedAnnouncement.toAnnouncement === 'string') {
                if (selectedAnnouncement.toAnnouncement.trim() === '') {
                    updatedAnnouncement.toAnnouncement = config.institutions.map(x => x.inst);
                } else {
                    updatedAnnouncement.toAnnouncement = selectedAnnouncement.toAnnouncement.split(',').filter(item => item.trim() !== '');
                }
            } else if (!Array.isArray(selectedAnnouncement.toAnnouncement)) {
                updatedAnnouncement.toAnnouncement = config.institutions.map(x => x.inst);
            }
            setAnnouncementObj(updatedAnnouncement);
        } else {
            setAnnouncementObj(empty_announcementObj);
        }
    }, [selectedAnnouncement]);

    const handleChange = name => (e) => {
        setAnnouncementObj({
            ...announcementObj,
            [name]: e.target.value
        });
    };

    const handleSwitchChange = (name) => (event) => {
        setAnnouncementObj({
            ...announcementObj,
            [name]: event.target.checked ? 1 : 0
        });
    };

    const handleToAnnouncementChange = (event, newValue) => {
        setAnnouncementObj({
            ...announcementObj,
            toAnnouncement: newValue || []
        });
    };

    const setLink = useCallback(() => {
        if (!editor) return;
        const previousUrl = editor.getAttributes('link').href;
        const url = window.prompt('Link URL', previousUrl || 'https://');
        if (url !== null) {
            if (url === '') {
                editor.chain().focus().extendMarkRange('link').unsetLink().run();
            } else {
                editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
            }
        }
    }, [editor]);

    const handleAddUpdateAnnouncementInfo = () => {
        onClose();
        const toAnnouncementString = Array.isArray(announcementObj.toAnnouncement)
            ? announcementObj.toAnnouncement.join(',')
            : announcementObj.toAnnouncement;
        const announcementData = {
            ...announcementObj,
            toAnnouncement: toAnnouncementString,
            userId: authUser.id,
            updatedDate: moment().format('YYYY-MM-DD HH:mm:ss')
        };

        if (selectedAnnouncement) {
            Swal.fire({
                title: 'Duyuru Güncelleme İşlemi',
                text: "Güncellemek istediğinizden emin misiniz?",
                icon: 'info',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Evet Güncelle',
                cancelButtonText: "Vazgeç"
            }).then(async (result) => {
                if (result.isConfirmed) {
                    const result = await putUpdateAnnouncementApi(announcementData);
                    if (result.status) {
                        Swal.fire({
                            position: 'center',
                            icon: 'success',
                            title: 'Güncelleme başarılı.',
                            showConfirmButton: false,
                            timer: 1500
                        });
                        afterUpdateFunc && afterUpdateFunc({ ...announcementData });
                    }
                }
            });
        } else {
            Swal.fire({
                title: 'Duyuru Ekleme İşlemi',
                text: "Eklemek istediğinizden emin misiniz?",
                icon: 'info',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Evet Ekle',
                cancelButtonText: "Vazgeç"
            }).then(async (result) => {
                if (result.isConfirmed) {
                    const result = await postSaveAnnouncementApi(announcementData);
                    if (result.status) {
                        Swal.fire({
                            position: 'center',
                            icon: 'success',
                            title: 'Ekleme başarılı.',
                            showConfirmButton: false,
                            timer: 1500
                        });
                        addAfterTable({ id: result.data.insertId, ...announcementData });
                    }
                }
            });
        }
    };

    if (!editor) return null;

    return (
        <>
            <JumboCardQuick>
                <Grid container marginY={1} spacing={1} rowSpacing={1}>
                    <Grid item xs={12}>
                        <TextField
                            variant="outlined"
                            label="Duyuru Başlığı"
                            fullWidth
                            value={announcementObj['title']}
                            onChange={handleChange("title")}
                            required
                        />
                    </Grid>

                    <Grid item xs={6}>
                        <FormControl fullWidth>
                            <InputLabel>Kimden</InputLabel>
                            <Select
                                value={announcementObj['fromAnnouncement']}
                                onChange={handleChange("fromAnnouncement")}
                                label="Kimden"
                            >
                                <MenuItem value="Kurum">Kurum</MenuItem>
                                <MenuItem value="Yazılım">Yazılım</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={6}>
                        <Autocomplete
                            multiple
                            size="small"
                            id="to-announcement-autocomplete"
                            options={config.institutions.map(x => x.inst)}
                            value={Array.isArray(announcementObj['toAnnouncement']) ? announcementObj['toAnnouncement'] : []}
                            onChange={handleToAnnouncementChange}
                            getOptionLabel={(option) => option}
                            renderInput={(params) => (
                                <TextField {...params} label="Kime" variant="outlined" size="small" />
                            )}
                        />
                    </Grid>

                    <Grid item xs={6}>
                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={"tr"}>
                            <FormLabel component="legend">Eklenme Tarihi</FormLabel>
                            <DateTimePicker
                                placeholder="Tarih Seçiniz"
                                value={announcementObj.addedDate}
                                onChange={(newValue) => {
                                    const formattedDate = moment(newValue).format('YYYY-MM-DD HH:mm:ss');
                                    setAnnouncementObj({
                                        ...announcementObj,
                                        addedDate: formattedDate
                                    });
                                }}
                                renderInput={(params) => <TextField {...params} fullWidth />}
                                required
                            />
                        </LocalizationProvider>
                    </Grid>

                    <Grid item xs={6}>
                        <FormControl fullWidth>
                            <FormLabel component="legend">Duyuru Durumu</FormLabel>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={announcementObj['status'] === 1}
                                        onChange={handleSwitchChange("status")}
                                        color="primary"
                                    />
                                }
                                label={announcementObj['status'] === 1 ? "Açık" : "Kapalı"}
                            />
                        </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'medium' }}>
                            Duyuru İçeriği *
                        </Typography>
                        <Box
                            className="add-update-announcement-editor-wrap"
                            sx={{
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 1,
                                overflow: 'hidden',
                                minHeight: '300px',
                                '& .add-update-announcement-editor': {
                                    minHeight: '250px',
                                    padding: 2,
                                    outline: 'none',
                                    '& p': { margin: '0 0 0.5em 0' },
                                    '& h1': { fontSize: '1.5rem', margin: '0.5em 0' },
                                    '& h2': { fontSize: '1.25rem', margin: '0.5em 0' },
                                    '& h3': { fontSize: '1.1rem', margin: '0.5em 0' },
                                    '& ul, & ol': { paddingLeft: 24, margin: '0.5em 0' },
                                    '& blockquote': { borderLeft: '4px solid', borderColor: 'divider', paddingLeft: 2, margin: '0.5em 0' },
                                    '& a': { color: 'primary.main', textDecoration: 'underline' },
                                },
                                '& .tiptap p.is-editor-empty:first-child::before': {
                                    content: 'attr(data-placeholder)',
                                    color: 'text.disabled',
                                    float: 'left',
                                    pointerEvents: 'none',
                                },
                            }}
                        >
                            <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 1, py: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                <Button size="small" variant={editor.isActive('bold') ? 'contained' : 'outlined'} onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleBold().run(); }}>B</Button>
                                <Button size="small" variant={editor.isActive('italic') ? 'contained' : 'outlined'} onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleItalic().run(); }}>I</Button>
                                <Button size="small" variant={editor.isActive('heading', { level: 2 }) ? 'contained' : 'outlined'} onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleHeading({ level: 2 }).run(); }}>H2</Button>
                                <Button size="small" variant={editor.isActive('heading', { level: 3 }) ? 'contained' : 'outlined'} onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleHeading({ level: 3 }).run(); }}>H3</Button>
                                <Button size="small" variant={editor.isActive('bulletList') ? 'contained' : 'outlined'} onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleBulletList().run(); }}>• Liste</Button>
                                <Button size="small" variant={editor.isActive('orderedList') ? 'contained' : 'outlined'} onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleOrderedList().run(); }}>1. Liste</Button>
                                <Button size="small" variant={editor.isActive('link') ? 'contained' : 'outlined'} onMouseDown={(e) => { e.preventDefault(); setLink(); }}>Link</Button>
                                <Button size="small" variant={editor.isActive('blockquote') ? 'contained' : 'outlined'} onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleBlockquote().run(); }}>Alıntı</Button>
                            </Box>
                            <EditorContent editor={editor} />
                        </Box>
                    </Grid>
                </Grid>
            </JumboCardQuick>

            <br />
            <Card>
                <Box sx={{ margin: '8px' }}>
                    <Grid container justifyContent="flex-end" spacing={1}>
                        <Grid item>
                            <Button variant="contained" color="success" onClick={() => handleAddUpdateAnnouncementInfo()}>
                                {selectedAnnouncement ? "Güncelle" : "Kaydet"}
                            </Button>
                        </Grid>
                        <Grid item>
                            <Button variant="contained" color="error" onClick={() => onClose()}>
                                Kapat
                            </Button>
                        </Grid>
                    </Grid>
                </Box>
            </Card>
        </>
    );
}

export default AddUpdateAnnouncementCard;
